import { useEffect, useRef, useState } from 'react';
import { auth, provider } from '../../firebase';
import backgroundImage from '../../img/bg_image_texture3.png';
import LogoApp from '../../img/logoGntunes.svg';
import Footer from '../footer';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhoneInput from 'react-phone-input-2';
import { isLogin } from '../../_helpers/auth/secureToken';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { fetchUserTypeList, setLoader } from '../../redux';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import useFirebaseAuthentication from '../../_helpers/reusablefunctions/firebaseLoginFunctions';
import { errorLogin } from '../../_helpers/reusablefunctions/errorLogin';
import GoogleImg from '../../img/google.png';
import {
  captchaEnableService,
  captchaGenerateService,
} from '../../_services/auth/auth.service';
import {
  validateAuthPassword,
  validateAuthPhoneNumber,
  validateNomenclatureEmail,
} from '../../_helpers/reusablefunctions/validation-standard';

const textInfo = {
  fontSize: '12px',
  marginTop: '10px',
};

function PhoneAuth() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { from } = location.state || { from: { pathname: '/' } };
  const inputRefs = useRef([]);
  const otpLength = 6;
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState(Array(otpLength).fill(''));
  const [confirmationResult, setConfirmationResult] = useState(null);
  const setReCAPTCHAToken = () => {};
  const [error, setError] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [timer, setTimer] = useState(30);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [openEmailPhone, setOpenEmailPhone] = useState(true);
  const [errorEmail, setErrorEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [password, setPassword] = useState('');
  const [errorPass, setErrorPass] = useState(false);
  const [showPassword, setShowConfirmPass] = useState(false);
  const [showVerificationMesage, setShowVerificationMesage] = useState('');
  const [disabledBtn, setDisabledBtn] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaKey, setCaptchaKey] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { sendOTP, verifyOTP, loginClickHandler, registerClickHandler } =
    useFirebaseAuthentication({
      setConfirmationResult,
      confirmationResult,
      setReCAPTCHAToken,
      setErrorMsg,
      from: { pathname: '/' },
      phoneNumber,
      otpCode,
      setTimer,
      setDisabledBtn,
      setOtpCode,
    });

  // This forces the account chooser popup
  provider.setCustomParameters({
    prompt: 'select_account',
  });

  // ----------------phone and otp change handler ----------
  const phoneChangeHandler = (phone) => {
    setPhoneNumber(phone);
    phone.length !== 12 ? setError(true) : setError(false);
  };

  const handleChange = (index, value) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otpCode];
      newOtp[index] = value;
      setOtpCode(newOtp);
      if (value && index < otpLength - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  useEffect(() => {
    dispatch(fetchUserTypeList());
  }, [dispatch]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      setIsResendDisabled(true);
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
    }

    return () => clearInterval(interval);
  }, [timer]);

  const isValidPhoneNumber = (phone) =>
    validateAuthPhoneNumber(phone, { required: true }).isValid;

  const fetchCaptcha = async ({ resetInput = false } = {}) => {
    const captchaResponse = await captchaGenerateService();
    const captchaData = captchaResponse?.data || {};
    setCaptchaKey(captchaData.captcha_key || '');
    setCaptchaText(captchaData.captcha_value || '');
    if (resetInput) {
      setCaptchaValue('');
      setShowVerificationMesage('');
    }
    return captchaData;
  };

  useEffect(() => {
    if (!captchaRequired) return;

    const intervalId = setInterval(() => {
      fetchCaptcha({ resetInput: true });
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [captchaRequired]);

  const handleCaptchaEnable = async () => {
    if (!email) return null;
    return captchaEnableService(email);
  };

  // ------------------- Email and Password login
  const handleEmailLogin = async () => {
    if (isSubmitting) return;

    const normalizedEmail = email.trim().toLowerCase();

    const emailValidation = validateNomenclatureEmail(normalizedEmail);
    if (!emailValidation.isValid) {
      setErrorEmail(true);
      setEmailMsg('Invalid email format');
      return;
    }

    if (!validateAuthPassword(password).isValid) {
      setErrorPass(true);
      return;
    }

    setErrorPass(false);
    setErrorEmail(false);
    setShowVerificationMesage('');
    setEmailMsg('');
    setIsSubmitting(true);
    dispatch(setLoader(true));

    try {
      const captchaResponse = await handleCaptchaEnable();
      const required = Boolean(captchaResponse?.data?.captcha_required);

      if (required) {
        setCaptchaRequired(true);
        if (!captchaKey) await fetchCaptcha({ resetInput: true });
        if (!captchaValue.trim()) {
          setShowVerificationMesage('Please enter the captcha value');
          return;
        }
      } else {
        setCaptchaRequired(false);
        setCaptchaKey('');
        setCaptchaText('');
        setCaptchaValue('');
      }

      const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);

      if (!result.user.emailVerified) {
        setShowVerificationMesage('Please verify your email before login');
        return;
      }

      const extraPayload = required
        ? { captcha_key: captchaKey, captcha_value: captchaValue }
        : {};

      const loginResponse = await loginClickHandler(result.user.uid, extraPayload);

      if (loginResponse?.status === 200 || loginResponse?.status === 201) {
        history.push('/dashboard');
        return;
      }

      const apiError =
        loginResponse?.data?.error?.[0] ||
        loginResponse?.data?.message ||
        loginResponse?.data?.error ||
        'Login failed';

      setShowVerificationMesage(apiError);
    } catch (authError) {
      if (
        authError?.code === 'auth/wrong-password' ||
        authError?.code === 'auth/user-not-found'
      ) {
        setShowVerificationMesage('Invalid email or password');
      } else {
        setShowVerificationMesage(errorLogin(authError));
      }
    } finally {
      setIsSubmitting(false);
      dispatch(setLoader(false));
    }
  };

  // -------------- google login-------------
  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const loginResponse = await loginClickHandler(user?.uid);

      if (loginResponse?.data?.error?.[0] || (Array.isArray(loginResponse) && loginResponse[0])) {
        try {
          const registeredUser = await registerClickHandler(user.uid, { email: user?.email });

          if (registeredUser?.user?.profile?.uid === user?.uid) {
            await loginClickHandler(user.uid);
          } else {
            await loginClickHandler(user.uid);
          }
        } catch (registerError) {
          setErrorMsg(registerError);
          return;
        }
      }

      history.push('/');
    } catch (googleError) {
      setErrorMsg(errorLogin(googleError));
    }
  };

  if (isLogin()) {
    return <Redirect to={from.pathname} />;
  }

  return (
    <Box
      className="container-fluid"
      style={{
        height: '100vh',
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <Box className="row">
        <nav className="navbar navbar-light border pb-0">
          <Box className="container-fluid">
            <a className="navbar-brand p-3" href="https://gntunes.com/">
              <img src={LogoApp} alt="logo" />
            </a>
          </Box>
        </nav>
        <Box style={{ maxWidth: '600px', width: '100%', margin: 'auto' }}>
          <Box className="mt-4 mb-4 p-4 border rounded shadow bg-white">
            {!openEmailPhone ? (
              <form action="" method="" className="row g-3">
                <Box id="recaptcha-container"></Box>
                {!disabledBtn ? (
                  <>
                    <h4 className="text-center mt-0">Enter Phone Number </h4>
                    <PhoneInput
                      country={'in'}
                      value={phoneNumber}
                      name="phone_number"
                      onChange={phoneChangeHandler}
                      specialLabel="Enter phone number"
                    />
                    {(error || errorMsg) && (
                      <Typography color="error" variant="caption" display="block" gutterBottom>
                        {errorMsg || 'Please enter valid number'}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={textInfo}>
                      You will receive an SMS verification that may apply message and data rates.
                    </Typography>
                    <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                      <Button
                        variant="text"
                        onClick={() => setOpenEmailPhone(true)}
                        className="gn-actionbtn-color justify-content-start text-capitalize"
                      >
                        Sign In with Email
                      </Button>
                      <Button
                        variant="contained"
                        className="gn-actionbtn px-3 ms-3"
                        onClick={sendOTP}
                        disabled={!isValidPhoneNumber(phoneNumber)}
                      >
                        Send OTP
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <h4 className="text-center mt-0">Enter authentication code </h4>
                    <Typography variant="body2" className="text-center">
                      Enter the 6-digit that we have sent via the phone number + {phoneNumber}
                    </Typography>
                    <FormControl error={Boolean(errorMsg)}>
                      <Box display="flex" justifyContent="center" gap={2}>
                        {otpCode.map((digit, index) => (
                          <TextField
                            key={index}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            inputRef={(ref) => (inputRefs.current[index] = ref)}
                            variant="outlined"
                            size="small"
                            inputProps={{
                              maxLength: 1,
                              style: {
                                textAlign: 'center',
                                fontSize: '14px',
                                width: '20px',
                                height: '30px',
                              },
                            }}
                          />
                        ))}
                      </Box>
                      <FormHelperText className="text-center mt-2">{errorMsg}</FormHelperText>
                    </FormControl>
                    <Box display={'flex'} textAlign={'center'} justifyContent={'space-between'}>
                      <Button
                        variant="text"
                        className="gn-actionbtn-color"
                        onClick={sendOTP}
                        disabled={isResendDisabled}
                      >
                        Resend OTP {isResendDisabled ? `(${timer}s)` : ''}
                      </Button>
                      <Button
                        variant="contained"
                        className="gn-actionbtn px-3 ms-3"
                        onClick={verifyOTP}
                        disabled={error}
                      >
                        Verify OTP
                      </Button>
                    </Box>
                    <Typography variant="body2" sx={{ ...textInfo, textAlign: 'center' }}>
                      By continuing, you agree to our
                      <a
                        href="https://gntunes.com/terms-condition"
                        className="gn-actionbtn-color fw-bold text-decoration-none"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {' '}
                        Terms of Service
                      </a>{' '}
                      and
                      <a
                        href="https://gntunes.com/privacy"
                        className="gn-actionbtn-color fw-bold text-decoration-none"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {' '}
                        Privacy Policy
                      </a>
                      .
                    </Typography>
                  </>
                )}
              </form>
            ) : (
              <Box>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleEmailLogin();
                  }}
                  action=""
                  method=""
                  className="row g-3"
                >
                  <h4 className="text-center "> Sign In </h4>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: 0 }}>
                    <IconButton
                      sx={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '100%',
                        color: '#fff',
                      }}
                      onClick={handleGoogleSignIn}
                    >
                      <img src={GoogleImg} alt="google-img" className="w-100" />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: 300 }}>
                      <Divider sx={{ flexGrow: 1 }} />
                      <Typography sx={{ mx: 2, whiteSpace: 'nowrap' }}>or</Typography>
                      <Divider sx={{ flexGrow: 1 }} />
                    </Box>
                  </Box>
                  {showVerificationMesage && (
                    <span className="text-bg-danger fs-08 text-center m-0">{showVerificationMesage}</span>
                  )}
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    error={errorEmail}
                    helperText={errorEmail ? emailMsg : ''}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    margin="normal"
                    size="small"
                  />

                  <TextField
                    label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    margin="normal"
                    size="small"
                    type={showPassword ? 'text' : 'password'}
                    error={errorPass}
                    helperText={
                      'Password must contain at least 8 characters, including at least 1 upper case letter, 1 lower case letter,1 number and 1 special character.'
                    }
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={() => setShowConfirmPass(!showPassword)}>
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      ),
                    }}
                  />

                  {captchaRequired && (
                    <>
                      <Box className="d-flex align-items-center justify-content-between mt-1">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Enter captcha
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => fetchCaptcha({ resetInput: true })}
                          aria-label="Refresh captcha"
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px',
                          px: 1.5,
                          py: 1,
                          backgroundColor: '#fafafa',
                          fontWeight: 700,
                          letterSpacing: 1,
                          width: 'fit-content',
                        }}
                      >
                        {captchaText || '-----'}
                      </Box>
                      <TextField
                        label="Captcha"
                        value={captchaValue}
                        onChange={(event) => setCaptchaValue(event.target.value)}
                        fullWidth
                        margin="normal"
                        size="small"
                      />
                    </>
                  )}

                  <Box className="mt-1">
                    <a href="/forgot-password" className="gn-actionbtn-color fw-bold text-decoration-none fs-09">
                      Forgot Password?
                    </a>
                  </Box>
                  <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} mt={1}>
                    <Typography variant="body2">
                      <span> Create account? </span>
                      <a href="/register" className="gn-actionbtn-color fw-bold text-decoration-none">
                        Sign Up
                      </a>
                      <span> or </span>
                      <span
                        onClick={() => setOpenEmailPhone(false)}
                        className="cursorPointer gn-actionbtn-color fw-bold text-decoration-none"
                      >
                        Login with phone
                      </span>
                    </Typography>
                    <Button
                      type="submit"
                      variant="contained"
                      className="gn-actionbtn px-3 ms-3"
                      sx={{ mt: 1 }}
                      disabled={isSubmitting}
                    >
                      Sign In
                    </Button>
                  </Box>
                  <Typography variant="body2" sx={{ marginTop: '20px', textAlign: 'center' }}>
                    By continuing, you agree to our
                    <a
                      href="https://gntunes.com/terms-condition"
                      className="gn-actionbtn-color fw-bold text-decoration-none"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {' '}
                      Terms of Service
                    </a>{' '}
                    and
                    <a
                      href="https://gntunes.com/privacy"
                      className="gn-actionbtn-color fw-bold text-decoration-none"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {' '}
                      Privacy Policy
                    </a>
                    .
                  </Typography>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
}

export default PhoneAuth;
